import {useEffect, useMemo} from 'react'
import type {Thread} from '@github-ui/diff-lines/conversation-types'
import {useMarkersActions} from '../stores/selectors'
import {useThreadPreviewPositioning} from '../page-data/loaders/use-thread-preview-positioning'
import {positionedThreadToOriginalPositioning} from '../helpers/thread-positioning'

const POSITIONING_PAGE_SIZE = 40

/**
 * Fetches historical positioning for threads that do not have it.
 *
 * @param threadPreviews - Array of threads to process
 */
export function useHydrateThreadPositioning(threadPreviews: Thread[]) {
  const {updateThreads} = useMarkersActions()

  const threadsNeedingPositioning = useMemo(
    () =>
      threadPreviews
        .filter(thread => !thread.originalPositioning)
        .map(thread => Number(thread.id))
        .slice(0, POSITIONING_PAGE_SIZE),
    [threadPreviews],
  )

  const {data: positioningData} = useThreadPreviewPositioning(threadsNeedingPositioning)

  useEffect(() => {
    if (!positioningData || Object.keys(positioningData.threads).length === 0) return

    updateThreads(threads => {
      return threads.map(thread => {
        const positioning = positioningData.threads[thread.id]
        if (!positioning) return thread

        return {
          ...thread,
          originalPositioning: positionedThreadToOriginalPositioning(positioning, thread.originalPositioning?.path),
        }
      })
    })
  }, [positioningData, updateThreads])
}
